import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from './AuthProvider';
import { getLoginRedirectUrl, config } from '../services/config';
import { useAppIdentity } from '../hooks/useAppIdentity';
import { isRunningStandalone } from '../utils/evoDetection';
import { loadOIDCState, clearOIDCState } from '../utils/oidcStateManager';

interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: JSX.Element;
}

/**
 * AuthGuard component that protects routes and redirects unauthenticated users
 * to the Access Group login page
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({ 
  children, 
  fallback 
}): JSX.Element => {
  const { isAuthenticated, isLoading } = useAuth();
  const identity = useAppIdentity();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [redirectTimer, setRedirectTimer] = useState<NodeJS.Timeout | null>(null);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const [callbackProcessingTime, setCallbackProcessingTime] = useState<number | null>(null);
  const redirectingRef = useRef(false);
  
  // Determine if we're running in standalone mode
  const standalone = isRunningStandalone();
  
  // Check for saved OIDC state if current identity is not authenticated
  const savedOIDCState = standalone && !identity.isAuthenticated ? loadOIDCState() : null;
  
  // Simple authentication state - use enhanced auth state for basic testing
  // Also consider saved OIDC state as a fallback
  const effectiveIsAuthenticated = standalone 
    ? (isAuthenticated || identity.isAuthenticated || (savedOIDCState?.isAuthenticated ?? false))
    : isAuthenticated;
  const effectiveIsLoading = standalone
    ? (isLoading || identity.isLoading)
    : isLoading;

  // Basic callback detection and logging
  useEffect(() => {
    const hasCallbackParams = window.location.search.includes('code=');
    const isCallbackRoute = window.location.pathname === '/login/callback';
    
    if (hasCallbackParams || isCallbackRoute) {
      if (!callbackProcessingTime) {
        setCallbackProcessingTime(Date.now());
      }
    }
  }, [callbackProcessingTime]);

  useEffect(() => {
    // Mark that we've completed the initial auth check
    if (!effectiveIsLoading) {
      setHasCheckedAuth(true);
    }
  }, [effectiveIsLoading]);

  // Helper function to perform redirect based on mode
  const performRedirect = async () => {
    if (redirectingRef.current) {
      return;
    }
    
    redirectingRef.current = true;
    sessionStorage.setItem('lastAuthRedirect', Date.now().toString());
    setShouldRedirect(true);
    
    if (standalone) {
      // In standalone mode, use identity-react's OIDC flow
      try {
        await identity.signinRedirect();
      } catch (error) {
        console.error('Failed to initiate signin redirect:', error);
        redirectingRef.current = false;
        // Fall back to Workspace-managed login if OIDC fails
        const redirectUrl = getLoginRedirectUrl();
        window.location.href = redirectUrl;
      }
    } else {
      // In Evo tab mode, use Workspace-managed login
      const redirectUrl = getLoginRedirectUrl();
      window.location.href = redirectUrl;
    }
  };

  useEffect(() => {
    // Don't redirect while still loading authentication state
    if (effectiveIsLoading || !hasCheckedAuth) {
      return;
    }

    // If not authenticated, set up redirect after a delay to avoid loops
    if (!effectiveIsAuthenticated) {
      // Check if we just had a successful authentication
      const recentAuthSuccess = sessionStorage.getItem('oidc_auth_success');
      const now = Date.now();
      const recentSuccess = recentAuthSuccess && (now - parseInt(recentAuthSuccess)) < 10000; // 10 seconds
      
      if (recentSuccess) {
        // Give IdentityProvider more time to restore authentication state from localStorage
        return;
      }
      
      // Check if we're returning from Access Group login (has query params or hash)
      const hasCallbackParams = window.location.search.includes('code=') || window.location.hash;
      const isCallbackRoute = window.location.pathname === '/login/callback';
      
      // If we're on callback route or have callback params, give more time for processing
      if (isCallbackRoute || hasCallbackParams) {
        const processingTime = callbackProcessingTime ? Date.now() - callbackProcessingTime : 0;
        
        // Don't redirect immediately if we're still processing the callback
        if (processingTime < 20000) { // Wait up to 20 seconds for callback processing
          return;
        } else {
          console.warn('AuthGuard: Callback processing timeout, proceeding with redirect');
        }
      }
      
      // Check for recent redirects to prevent loops
      const lastRedirect = sessionStorage.getItem('lastAuthRedirect');
      const recentRedirect = lastRedirect && (now - parseInt(lastRedirect)) < 30000; // 30 seconds
      
      if (recentRedirect) {
        return; // Don't redirect again if we just did
      }
      
      const redirectDelay = hasCallbackParams ? 10000 : 3000; // Wait longer if returning from login
      
      const timer = setTimeout(() => {
        performRedirect();
      }, redirectDelay);

      setRedirectTimer(timer);
      
      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    } else {
      // User is authenticated, clear any pending redirects and redirect timestamps
      sessionStorage.removeItem('lastAuthRedirect');
      sessionStorage.removeItem('oidc_auth_success'); // Clean up success marker
      setShouldRedirect(false);
      redirectingRef.current = false;
      if (redirectTimer) {
        clearTimeout(redirectTimer);
        setRedirectTimer(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveIsAuthenticated, effectiveIsLoading, hasCheckedAuth, standalone, callbackProcessingTime]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (redirectTimer) {
        clearTimeout(redirectTimer);
      }
    };
  }, [redirectTimer]);

  // Clear saved OIDC state once identity provider has loaded the user
  useEffect(() => {
    if (standalone && identity.isAuthenticated && identity.user && savedOIDCState) {
      clearOIDCState();
    }
  }, [standalone, identity.isAuthenticated, identity.user, savedOIDCState]);

  // Show loading state while authentication is being checked
  if (effectiveIsLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem'
      }}>
        <div style={{ 
          fontSize: '1.2rem', 
          marginBottom: '1rem',
          color: '#666' 
        }}>
          Loading...
        </div>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #0066cc',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Show authentication required message
  if (!effectiveIsAuthenticated) {
    if (fallback) {
      return fallback;
    }
    
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <h2 style={{ color: '#333', marginBottom: '1rem' }}>
          Authentication Required
        </h2>
        <p style={{ color: '#666', marginBottom: '2rem', maxWidth: '400px' }}>
          You need to authenticate with Access Group to access the Feedback Dashboard.
          {shouldRedirect && ' Redirecting to login page...'}
        </p>
        
        {shouldRedirect ? (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            color: '#0066cc',
            fontSize: '1.1rem' 
          }}>
            <div style={{ 
              width: '20px', 
              height: '20px', 
              border: '3px solid #f3f3f3',
              borderTop: '3px solid #0066cc',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginRight: '10px'
            }} />
            Redirecting to Access Group login...
          </div>
        ) : (
          <button
            onClick={() => {
              performRedirect();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              backgroundColor: '#E11D48',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              minWidth: '250px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#BE185D';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#E11D48';
            }}
          >
            {/* Access Logo */}
            <svg 
              className="logoSVG" 
              version="1.1" 
              id="Layer_1" 
              xmlns="http://www.w3.org/2000/svg" 
              x="0px" 
              y="0px" 
              viewBox="0 0 500 116"
              style={{ 
                height: '20px', 
                width: 'auto',
                flexShrink: 0
              }}
            >
              <g>
                <g>
                  <path d="M144.9,58.3L95.8,14.8l0,0C85.5,5.7,72.3,0.4,57.6,0.4C25.9,0.4,0,26.3,0,58s25.9,57.6,57.6,57.6 c14.7,0,27.9-5.6,38.2-14.4l0,0L144.9,58.3z M17.3,58.3c0-22,17.9-40.3,40.3-40.3c22,0,40.3,17.9,40.3,40.3 c0,22-17.9,40.3-40.3,40.3C35.6,98.3,17.3,80.3,17.3,58.3" style={{fill: 'rgb(255, 255, 255)'}}></path>
                </g>
                <g>
                  <path d="M165.5,73.3c0-11.8,9.8-20,38.5-24v-2.8c0-7.1-3.9-10.6-11-10.6c-8.6,0-14.9,3.9-20.8,8.6l-5.5-5.1 c7.1-6.7,16.1-11.4,27.5-11.4c13.8,0,19.3,7.1,19.3,19.3v25.5c0,7.1,0.4,11,1.6,14.1h-9.8c-0.8-2.4-1.2-4.7-1.2-7.9 c-6.3,5.9-13.8,8.6-22,8.6C171.8,87.9,165.5,82.8,165.5,73.3 M204,72.1V56c-22,3.5-29.1,8.6-29.1,16.1c0,5.5,3.5,8.6,9.8,8.6 C191.9,80.4,198.5,77.3,204,72.1" style={{fill: 'rgb(255,255,255)'}}></path>
                </g>
                <g>
                  <path d="M225.7,58.4c0-20.8,13.4-29.9,26.3-29.9c13,0,18.9,7.5,22.4,16.1l-7.9,3.1c-2.8-7.5-7.5-11.4-14.9-11.4 c-8.3,0-16.5,5.9-16.5,22c0,14.1,6.7,22,16.5,22c7.5,0,11.8-3.1,15.3-11.4l7.9,3.1c-4.3,9.4-11,16.5-23.6,16.5 C238.2,88.3,225.7,78,225.7,58.4" style={{fill: 'rgb(255,255,255)'}}></path>
                </g>
                <g>
                  <path d="M283,58.4c0-20.8,13.4-29.9,26.3-29.9s18.9,7.5,22.4,16.1l-7.9,3.1c-2.8-7.5-7.5-11.4-14.9-11.4 c-8.3,0-16.5,5.9-16.5,22c0,14.1,6.7,22,16.5,22c7.5,0,11.8-3.1,15.3-11.4l7.9,3.1c-4.3,9.4-11,16.5-23.6,16.5 C295.6,88.3,283,78,283,58.4" style={{fill: 'rgb(255,255,255)'}}></path>
                </g>
                <g>
                  <path d="M340.4,58.4c0-20.8,13.8-29.9,25.9-29.9c13.8,0,24,9.4,24,30.3v2h-40.5c0.4,13,7.9,20,17.3,20 c8.3,0,13-3.5,17.3-9.4l5.5,4.7c-5.5,7.5-12.6,12.2-23.6,12.2C353,88.3,340.4,78.4,340.4,58.4 M381.7,53.3 c-0.8-10.2-5.5-17.3-14.9-17.3c-7.9,0-14.9,5.5-16.5,17.3H381.7z" style={{fill: 'rgb(255,255,255)'}}></path>
                </g>
                <g>
                  <path d="M398.6,78.4l5.5-5.5c4.3,4.7,10.2,7.9,18.1,7.9c9,0,13.8-3.1,13.8-9c0-5.9-5.1-7.9-14.9-10.2 c-12.2-2.8-20.4-6.3-20.4-17.3c0-8.6,7.5-15.7,20-15.7c10.2,0,16.5,3.1,22,8.3l-4.7,6.3c-4.3-3.9-10.2-6.7-16.5-6.7 c-8.3,0-11.8,3.5-11.8,7.9c0,5.5,4.7,7.5,13.8,9.4c13,3.1,22,6.3,22,18.1c0,11-9.8,16.9-22.8,16.9C412,87.9,403.3,83.9,398.6,78.4 " style={{fill: 'rgb(255,255,255)'}}></path>
                </g>
                <g>
                  <path d="M453.2,78.4l5.5-5.5c4.3,4.7,10.2,7.9,18.1,7.9c9,0,13.8-3.1,13.8-9c0-5.9-5.1-7.9-14.9-10.2 c-12.2-2.8-20.4-6.3-20.4-17.3c0-8.6,7.5-15.7,20-15.7c10.2,0,16.5,3.1,22,8.3l-4.7,6.3c-4.3-3.9-10.2-6.7-16.5-6.7 c-8.3,0-11.8,3.5-11.8,7.9c0,5.5,4.7,7.5,13.8,9.4c13,3.1,22,6.3,22,18.1c0,11-9.8,16.9-22.8,16.9 C466.6,87.9,457.9,83.9,453.2,78.4" style={{fill: 'rgb(255,255,255)'}}></path>
                </g>
              </g>
            </svg>
            <span>credentials</span>
          </button>
        )}

        <div style={{ 
          marginTop: '3rem', 
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          maxWidth: '500px'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: '#333' }}>
            Environment: {config.environment}
          </h3>
          <div style={{ fontSize: '0.9rem', color: '#666', textAlign: 'left' }}>
            <div><strong>Login URL:</strong> {config.loginUrl}</div>
            <div><strong>Return URL:</strong> {config.returnUrl}</div>
            <div><strong>API Base:</strong> {config.apiBaseUrl}</div>
          </div>
        </div>
      </div>
    );
  }

  // User is authenticated, render children
  return <>{children}</>;
};

export default AuthGuard;
